import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import BottomDrawer from '../../Drawer/Drawer';
import Button from '../../Button/Button';
import { ReactComponent as RadioOn } from '../../../assets/images/svg/radio-on.svg';
import { ReactComponent as RadioOff } from '../../../assets/images/svg/radio-off.svg';
import { ReactComponent as AmberAlertTriangle } from '../../../assets/images/svg/amber-alert-triangle.svg';
import { pxToRem } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';

const OTHER = 'other';
const REASONS = ['changedMind', 'foundBetterPrice', 'orderedByMistake', OTHER];

const HeaderWrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${pxToRem(8)};
  padding-top: ${pxToRem(8)};
`;

const AttentionIcon = styled.div`
  width: ${pxToRem(64)};
  height: ${pxToRem(64)};
  border-radius: 50%;
  background: ${themeColors.colorBackgroundSecondary};
  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    width: ${pxToRem(32)};
    height: ${pxToRem(32)};
  }
`;

const Title = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(18)};
  font-weight: 600;
  color: ${themeColors.colorTextPrimary};
`;

const Subtitle = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  color: ${themeColors.colorTextSecondary};
`;

const NoticeBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${pxToRem(10)};
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: ${pxToRem(4)};
  padding: ${pxToRem(12)} ${pxToRem(16)};
  margin-top: ${pxToRem(16)};
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  line-height: 1.5;
  color: ${themeColors.colorTextSecondary};

  > svg {
    width: ${pxToRem(20)};
    height: ${pxToRem(20)};
    flex-shrink: 0;
    margin-top: ${pxToRem(2)};
  }
`;

const OptionsList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${pxToRem(12)};
  margin-top: ${pxToRem(16)};
  margin-bottom: ${pxToRem(-12)};
`;

const OptionCard = styled.button`
  display: flex;
  align-items: center;
  gap: ${pxToRem(12)};
  width: 100%;
  text-align: left;
  cursor: pointer;
  background: ${themeColors.colorBackgroundSecondary};
  border: 1px solid
    ${({ $selected }) =>
      $selected ? themeColors.colorTextSecondary : 'transparent'};
  border-radius: ${pxToRem(4)};
  padding: ${pxToRem(12)} ${pxToRem(16)};
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  color: ${themeColors.colorTextPrimary};

  &:focus {
    outline: none;
  }
`;

const RadioIndicator = styled.span`
  display: flex;
  flex-shrink: 0;
  width: ${pxToRem(20)};
  height: ${pxToRem(20)};

  > svg {
    width: ${pxToRem(20)};
    height: ${pxToRem(20)};
  }
`;

const ReasonInput = styled.input`
  grid-column: 1 / -1;
  width: 100%;
  border: none;
  border-radius: ${pxToRem(4)};
  padding: ${pxToRem(12)} ${pxToRem(16)};
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  color: ${themeColors.colorTextPrimary};
  background: ${themeColors.colorBackgroundSecondary};
  box-sizing: border-box;

  &::placeholder {
    color: ${themeColors.colorTextSecondary};
  }

  &:focus {
    outline: none;
  }
`;

const FooterRow = styled.div`
  display: flex;
  gap: ${pxToRem(12)};

  > button {
    flex: 1;
  }
`;

const SellFromHomeCancelOrderDrawer = ({
  isOpen,
  onClose,
  onConfirm,
  subtitle,
}) => {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState(null);
  const [otherText, setOtherText] = useState('');
  const inputRef = useRef(null);

  const isOther = selectedReason === OTHER;
  // Disabled until a reason is picked; if "Other", until the free-text is typed.
  const confirmDisabled = !selectedReason || (isOther && !otherText.trim());

  // When "Other" is picked, focus the free-text field and scroll it into view.
  useEffect(() => {
    if (isOther && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isOther]);

  const handleConfirm = () => {
    if (confirmDisabled) return;
    onConfirm(isOther ? otherText.trim() : selectedReason);
  };

  const footer = (
    <FooterRow>
      <Button
        variant="primaryOutlined"
        textTransform="none"
        disabled={confirmDisabled}
        onClick={handleConfirm}
        data-cy="button-sfh-confirm-cancel"
      >
        {t('sellWizard.sfhWizard.cancelDrawer.confirm')}
      </Button>
      <Button
        variant="primary"
        textTransform="none"
        onClick={onClose}
        data-cy="button-sfh-dismiss-cancel"
      >
        {t('sellWizard.sfhWizard.cancelDrawer.dismiss')}
      </Button>
    </FooterRow>
  );

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      footer={footer}
      maxHeight="85%"
    >
      <HeaderWrap>
        <AttentionIcon>
          <AmberAlertTriangle />
        </AttentionIcon>
        <Title>{t('sellWizard.sfhWizard.cancelDrawer.title')}</Title>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </HeaderWrap>

      <NoticeBox>
        <AmberAlertTriangle />
        <span>
          {t('sellWizard.sfhWizard.transactionHistory.marketLossNotice')}
        </span>
      </NoticeBox>

      <OptionsList data-cy="list-sfh-cancel-reasons">
        {REASONS.map((reason) => {
          const selected = selectedReason === reason;
          return (
            <OptionCard
              key={reason}
              type="button"
              $selected={selected}
              onClick={() => setSelectedReason(reason)}
              data-cy={`radio-sfh-reason-${reason}`}
            >
              <RadioIndicator>
                {selected ? <RadioOn /> : <RadioOff />}
              </RadioIndicator>
              <span>
                {t(`sellWizard.sfhWizard.cancelDrawer.reason_${reason}`)}
              </span>
            </OptionCard>
          );
        })}
        {isOther && (
          <ReasonInput
            ref={inputRef}
            type="text"
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            placeholder={t(
              'sellWizard.sfhWizard.cancelDrawer.otherPlaceholder',
            )}
            data-cy="input-sfh-cancel-reason"
          />
        )}
      </OptionsList>
    </BottomDrawer>
  );
};

SellFromHomeCancelOrderDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  subtitle: PropTypes.string,
};

export default SellFromHomeCancelOrderDrawer;
